
import { Component, Vue } from 'vue-property-decorator'
import HeadLable from '@/components/HeadLable/index.vue'
import {
  getDishPage,
  deleteDish,
  dishStatusByStatus,
  dishSetSort,
  dishSetInviteBucket
} from '@/api/dish'
import { getCategoryList } from '@/api/category'
import InputAutoComplete from '@/components/InputAutoComplete/index.vue'
import Empty from '@/components/Empty/index.vue'
import SortInlineEdit from '@/components/SortInlineEdit/index.vue'
import { UserModule } from '@/store/modules/user'

const DISH_TREE_EXPAND_KEY = 'admin-dish-tree-expanded'
const DISH_TREE_SCROLL_KEY = 'admin-dish-tree-scroll'

@Component({
  name: 'DishType',
  components: {
    HeadLable,
    InputAutoComplete,
    Empty,
    SortInlineEdit
  }
})
export default class extends Vue {
  private input: any = ''
  private checkList: string[] = []
  private tableData: any[] = []
  private allCategoryList: any[] = []
  private dishState = ''
  private dishStatus = ''
  private isSearch: boolean = false
  private restoringTree = false
  private saleStatus: any = [
    { value: 0, label: '下架' },
    { value: 1, label: '上架' }
  ]

  created() {
    this.init()
  }

  mounted() {
    window.addEventListener('resize', this.layoutDishTable)
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.layoutDishTable)
  }

  activated() {
    this.$nextTick(() => {
      this.restoreTreeUiState()
      this.layoutDishTable()
    })
  }

  initProp(val) {
    this.input = val
    this.initFun()
  }

  initFun() {
    this.init()
  }

  private isDishRow(row: any) {
    return row && row.nodeType === 'dish'
  }

  private isCategoryRow(row: any) {
    return row && row.nodeType === 'category'
  }

  private isDishSelectable(row: any) {
    return this.isDishRow(row)
  }

  private getCategoryParentId(item: any) {
    if (!item) return null
    const p = item.parentId != null ? item.parentId : item.parent_id
    if (p == null || p === '' || Number(p) === 0) return null
    return Number(p)
  }

  private getCategoryDepth(row: any) {
    if (!row || row.id === 'uncategorized') return 0
    let depth = 1
    let pid = this.getCategoryParentId(row)
    const map: Record<string, any> = {}
    this.allCategoryList.forEach((c: any) => {
      if (c && c.id != null) map[String(c.id)] = c
    })
    let guard = 0
    while (pid != null && map[String(pid)] && guard < 8) {
      depth++
      pid = this.getCategoryParentId(map[String(pid)])
      guard++
    }
    return depth
  }

  private getLevelLabel(row: any) {
    if (!this.isCategoryRow(row)) return ''
    if (row.id === 'uncategorized') return ''
    const d = this.getCategoryDepth(row)
    if (d <= 1) return '一级'
    if (d === 2) return '二级'
    if (d === 3) return '三级'
    return `${d}级`
  }

  /** 三级品牌映射到所属二级，便于列表不再展示三级节点 */
  private toDisplayMountCategoryId(rawId: any): string | null {
    if (rawId == null || rawId === '') return null
    const map: Record<string, any> = {}
    ;(this.allCategoryList || []).forEach((c: any) => {
      if (c && c.id != null) map[String(c.id)] = c
    })
    let cur = map[String(rawId)]
    if (!cur) return null
    let guard = 0
    while (cur && this.getCategoryDepth(cur) > 2 && guard < 8) {
      const pid = this.getCategoryParentId(cur)
      cur = pid != null ? map[String(pid)] : null
      guard++
    }
    if (!cur || this.getCategoryDepth(cur) > 2) return null
    return String(cur.id)
  }

  /** 一级：二级个数；二级/未分类：商品个数 */
  private attachDishTreeDisplayCounts(roots: any[]) {
    const walk = (list: any[]) => {
      ;(list || []).forEach((n: any) => {
        if (!n || n.nodeType !== 'category') return
        const kids = Array.isArray(n.children) ? n.children : []
        if (n.id === 'uncategorized') {
          n.displayCount = kids.filter((c: any) => c && c.nodeType === 'dish').length
          return
        }
        const depth = this.getCategoryDepth(n)
        if (depth <= 1) {
          n.displayCount = kids.filter((c: any) => c && c.nodeType === 'category').length
        } else {
          n.displayCount = kids.filter((c: any) => c && c.nodeType === 'dish').length
        }
        walk(kids)
      })
    }
    walk(roots)
  }

  /** 分类树 + 商品叶子（不展示三级品牌；多展示分类则同一商品可出现多次） */
  private buildDishCategoryTree(categories: any[], dishes: any[]) {
    const nodes: Record<string, any> = {}
    ;(categories || []).forEach((item: any) => {
      if (!item || item.id == null) return
      if (this.getCategoryDepth(item) >= 3) return
      nodes[String(item.id)] = {
        ...item,
        nodeType: 'category',
        rowKey: 'c-' + item.id,
        children: [] as any[]
      }
    })

    const uncategorized: any[] = []
    ;(dishes || []).forEach((d: any) => {
      if (!d || d.id == null) return
      const mountIds = this.resolveDishMountCategoryIds(d)
      if (!mountIds.length) {
        uncategorized.push(this.toDishTreeNode(d, 'uncategorized'))
        return
      }
      let mounted = false
      mountIds.forEach((cid: string) => {
        if (!nodes[cid]) return
        nodes[cid].children.push(this.toDishTreeNode(d, cid))
        mounted = true
      })
      if (!mounted) {
        uncategorized.push(this.toDishTreeNode(d, 'uncategorized'))
      }
    })

    const roots: any[] = []
    Object.keys(nodes).forEach(key => {
      const node = nodes[key]
      const pid = this.getCategoryParentId(node)
      if (pid != null && nodes[String(pid)]) {
        nodes[String(pid)].children.push(node)
      } else {
        roots.push(node)
      }
    })

    const sortAndClean = (arr: any[]) => {
      arr.sort((a, b) => {
        const ta = a.nodeType === 'category' ? 0 : 1
        const tb = b.nodeType === 'category' ? 0 : 1
        if (ta !== tb) return ta - tb
        // 分类内商品：保持 dish.sort 升序（越小越靠前）
        const sa = Number(a.sort != null ? a.sort : 0)
        const sb = Number(b.sort != null ? b.sort : 0)
        if (sa !== sb) return sa - sb
        const idA = Number(a.id)
        const idB = Number(b.id)
        if (!isNaN(idA) && !isNaN(idB)) return idA - idB
        return String(a.rowKey || a.id).localeCompare(String(b.rowKey || b.id))
      })
      arr.forEach(n => {
        if (n.nodeType === 'dish') {
          delete n.children
          return
        }
        if (n.children && n.children.length) {
          sortAndClean(n.children)
        } else {
          delete n.children
        }
      })
    }
    sortAndClean(roots)

    if (uncategorized.length) {
      const uncNode = {
        id: 'uncategorized',
        name: '未分类',
        nodeType: 'category',
        rowKey: 'c-uncategorized',
        children: uncategorized
      }
      sortAndClean(uncNode.children)
      roots.push(uncNode)
    }

    const result = this.shouldPruneEmptyBranches()
      ? this.pruneEmptyCategories(roots)
      : roots
    this.attachDishTreeDisplayCounts(result)
    return result
  }

  /** 商品挂载分类：展示分类 ∪ 品牌分类；三级归并到二级后去重 */
  private resolveDishMountCategoryIds(dish: any): string[] {
    const ids: string[] = []
    const seen = new Set<string>()
    const push = (raw: any) => {
      const mapped = this.toDisplayMountCategoryId(raw)
      if (mapped == null) return
      if (seen.has(mapped)) return
      seen.add(mapped)
      ids.push(mapped)
    }
    const displayIds = dish.displayCategoryIds
    if (Array.isArray(displayIds)) {
      displayIds.forEach((id: any) => push(id))
    }
    push(dish.categoryId)
    return ids
  }

  private toDishTreeNode(dish: any, mountCategoryId: string) {
    const sorts = dish && dish.displayCategorySorts
    let mountSort: any = null
    if (sorts && typeof sorts === 'object') {
      mountSort =
        sorts[mountCategoryId] != null
          ? sorts[mountCategoryId]
          : sorts[Number(mountCategoryId)] != null
            ? sorts[Number(mountCategoryId)]
            : sorts[String(mountCategoryId)]
    }
    // 不再回退全局 dish.sort，避免三级下出现 30/31 这类全店序号
    return {
      ...dish,
      nodeType: 'dish',
      mountCategoryId,
      sort: mountSort,
      rowKey: 'd-' + dish.id + '-c-' + mountCategoryId
    }
  }

  private shouldPruneEmptyBranches() {
    const hasName = !!(this.input && String(this.input).trim())
    const hasStatus = this.dishStatus !== '' && this.dishStatus != null
    return hasName || hasStatus
  }

  private pruneEmptyCategories(arr: any[]): any[] {
    return (arr || []).filter((n: any) => {
      if (n.nodeType === 'dish') return true
      if (n.children && n.children.length) {
        n.children = this.pruneEmptyCategories(n.children)
        if (!n.children.length) {
          delete n.children
          return false
        }
        return true
      }
      return false
    })
  }

  private setSortHandle(row: any, sort: number) {
    if (!row || !row.id || !this.isDishRow(row)) return
    const categoryId = row.mountCategoryId
    if (categoryId == null || categoryId === '' || categoryId === 'uncategorized') {
      this.$message.warning('请先将商品挂到展示分类后再排序')
      return
    }
    dishSetSort({ id: row.id, categoryId, sort })
      .then((res: any) => {
        if (res && res.data && String(res.data.code) === '1') {
          this.$message.success('排序已更新')
          this.init()
        } else {
          this.$message.error((res.data && (res.data.msg || res.data.desc)) || '排序失败')
        }
      })
      .catch((err: any) => {
        this.$message.error('请求出错了：' + ((err && err.message) || '未知错误'))
      })
  }

  /** 同步树中同一商品多处挂载的邀请计桶状态 */
  private patchInviteBucketInTree(list: any[], dishId: any, inviteBucket: number) {
    ;(list || []).forEach((n: any) => {
      if (!n) return
      if (n.nodeType === 'dish' && String(n.id) === String(dishId)) {
        this.$set(n, 'inviteBucket', inviteBucket)
      }
      if (n.children && n.children.length) {
        this.patchInviteBucketInTree(n.children, dishId, inviteBucket)
      }
    })
  }

  private async onInviteBucketSwitch(row: any, on: boolean) {
    if (!row || !row.id || !this.isDishRow(row)) return
    const next: 0 | 1 = on ? 1 : 0
    const prev = Number(row.inviteBucket) === 1 ? 1 : 0
    if (next === prev) return
    this.patchInviteBucketInTree(this.tableData, row.id, next)
    try {
      const res: any = await dishSetInviteBucket({ id: row.id, inviteBucket: next })
      if (!res || !res.data || String(res.data.code) !== '1') {
        this.patchInviteBucketInTree(this.tableData, row.id, prev)
        this.$message.error((res && res.data && (res.data.msg || res.data.desc)) || '更新失败')
        return
      }
      this.$message.success(next === 1 ? '已开启邀请返现' : '已关闭邀请返现')
    } catch (err: any) {
      this.patchInviteBucketInTree(this.tableData, row.id, prev)
      this.$message.error('请求出错了：' + ((err && err.message) || '未知错误'))
    }
  }

  private async init(isSearch?) {
    this.isSearch = !!isSearch
    const storeId = UserModule.storeId
    if (!storeId) {
      this.tableData = []
      return
    }
    try {
      const [catRes, dishRes]: any[] = await Promise.all([
        getCategoryList({ storeId, type: 1 }),
        getDishPage({
          page: 1,
          pageSize: 1000,
          name: this.input || undefined,
          status: this.dishStatus !== '' && this.dishStatus != null ? this.dishStatus : undefined
        })
      ])
      if (String(catRes.data.code) !== '1') {
        this.$message.error(catRes.data.msg || '分类查询失败')
        return
      }
      if (String(dishRes.data.code) !== '1') {
        this.$message.error(dishRes.data.msg || '商品查询失败')
        return
      }
      const categories = catRes.data.data || []
      this.allCategoryList = categories
      const dishes =
        (dishRes.data.data && dishRes.data.data.records) || []
      this.tableData = this.buildDishCategoryTree(categories, dishes)
      this.restoreTreeUiState()
      this.$nextTick(() => this.layoutDishTable())
    } catch (err: any) {
      if (err && (err.message === '重复请求' || err.__CANCEL__)) return
      this.$message.error('请求出错了：' + ((err && err.message) || '未知错误'))
    }
  }

  private readExpandedKeys(): string[] {
    try {
      const raw = sessionStorage.getItem(DISH_TREE_EXPAND_KEY)
      const arr = raw ? JSON.parse(raw) : []
      return Array.isArray(arr) ? arr.map(String) : []
    } catch (_) {
      return []
    }
  }

  private writeExpandedKeys(keys: string[]) {
    sessionStorage.setItem(DISH_TREE_EXPAND_KEY, JSON.stringify(keys || []))
  }

  private layoutDishTable() {
    const table: any = this.$refs.dishTable
    if (table && typeof table.doLayout === 'function') {
      table.doLayout()
    }
  }

  private persistTreeUiState() {
    try {
      const wrap = this.$el && (this.$el as HTMLElement).querySelector('.el-table__body-wrapper')
      if (wrap) {
        sessionStorage.setItem(DISH_TREE_SCROLL_KEY, String((wrap as HTMLElement).scrollTop || 0))
      }
    } catch (_) {
      /* ignore */
    }
  }

  private handleExpandChange(row: any, expanded: any) {
    if (this.restoringTree) return
    if (!row || row.nodeType !== 'category' || !row.rowKey) return
    const isExpanded =
      typeof expanded === 'boolean'
        ? expanded
        : Array.isArray(expanded) && expanded.indexOf(row) > -1
    const set = new Set(this.readExpandedKeys())
    if (isExpanded) {
      set.add(String(row.rowKey))
    } else {
      set.delete(String(row.rowKey))
    }
    this.writeExpandedKeys(Array.from(set))
  }

  /** 修改某商品时，确保其挂载分类整条祖先链已记录为展开 */
  private ensureCategoryPathExpanded(mountCategoryId: any) {
    if (mountCategoryId == null || mountCategoryId === '' || mountCategoryId === 'uncategorized') {
      const keys = this.readExpandedKeys()
      if (!keys.includes('c-uncategorized')) {
        this.writeExpandedKeys(keys.concat(['c-uncategorized']))
      }
      return
    }
    const map: Record<string, any> = {}
    this.allCategoryList.forEach((c: any) => {
      if (c && c.id != null) map[String(c.id)] = c
    })
    const set = new Set(this.readExpandedKeys())
    let cur = map[String(mountCategoryId)]
    let guard = 0
    while (cur && cur.id != null && guard < 8) {
      set.add('c-' + cur.id)
      const pid = this.getCategoryParentId(cur)
      cur = pid != null ? map[String(pid)] : null
      guard++
    }
    this.writeExpandedKeys(Array.from(set))
  }

  private restoreTreeUiState() {
    const keys = this.readExpandedKeys()
    const scrollTop = Number(sessionStorage.getItem(DISH_TREE_SCROLL_KEY) || 0)
    this.$nextTick(() => {
      const table: any = this.$refs.dishTable
      if (!table || typeof table.toggleRowExpansion !== 'function') return
      this.restoringTree = true
      const keySet = new Set(keys)
      const expand = (list: any[]) => {
        ;(list || []).forEach((row: any) => {
          if (row && row.nodeType === 'category' && keySet.has(String(row.rowKey))) {
            table.toggleRowExpansion(row, true)
          }
          if (row && row.children && row.children.length) {
            expand(row.children)
          }
        })
      }
      expand(this.tableData)
      this.$nextTick(() => {
        this.restoringTree = false
        try {
          const wrap = this.$el && (this.$el as HTMLElement).querySelector('.el-table__body-wrapper')
          if (wrap && scrollTop > 0) {
            ;(wrap as HTMLElement).scrollTop = scrollTop
          }
        } catch (_) {
          /* ignore */
        }
      })
    })
  }

  private addDishtype(st: string, row?: any) {
    this.persistTreeUiState()
    if (row && this.isDishRow(row)) {
      this.ensureCategoryPathExpanded(row.mountCategoryId)
    }
    if (st === 'add') {
      this.$router.push({ path: '/dish/add' })
    } else {
      const query: any = { id: String(st) }
      if (row && row.mountCategoryId != null && row.mountCategoryId !== 'uncategorized') {
        query.mountCategoryId = String(row.mountCategoryId)
      }
      this.$router.push({ path: '/dish/add', query })
    }
  }

  private getCategoryNameById(categoryId: any): string {
    if (categoryId == null || categoryId === '' || categoryId === 'uncategorized') {
      return ''
    }
    const hit = (this.allCategoryList || []).find(
      (c: any) => c && String(c.id) === String(categoryId)
    )
    return hit && hit.name ? String(hit.name) : ''
  }

  /** 商品除当前挂载分类外，还挂在哪些分类 */
  private getOtherMountCategoryNames(row: any): string[] {
    if (!row || !this.isDishRow(row)) return []
    const current = row.mountCategoryId != null ? String(row.mountCategoryId) : ''
    const names: string[] = []
    const seen = new Set<string>()
    this.resolveDishMountCategoryIds(row).forEach((cid: string) => {
      if (!cid || cid === current || cid === 'uncategorized') return
      if (seen.has(cid)) return
      seen.add(cid)
      const name = this.getCategoryNameById(cid)
      names.push(name || `分类#${cid}`)
    })
    return names
  }

  private buildDeleteConfirmMessage(type: string, rowOrId: any): string {
    const tip =
      '删除后，该商品将从所有分类中彻底移除。若只是移出当前分类，请点击「修改」调整展示分类。'
    if (type === '批量') {
      return tip + '是否继续删除所选商品？'
    }
    const row =
      rowOrId && typeof rowOrId === 'object' && this.isDishRow(rowOrId)
        ? rowOrId
        : null
    if (!row) {
      return tip + '是否继续删除？'
    }
    const others = this.getOtherMountCategoryNames(row)
    const currentName =
      this.getCategoryNameById(row.mountCategoryId) || '当前分类'
    if (others.length) {
      return (
        tip +
        `<br/><br/>该商品除「${currentName}」外，还挂在：<strong>${others.join('、')}</strong>。是否继续删除？`
      )
    }
    return (
      tip +
      `<br/><br/>当前仅挂在「${currentName}」。是否继续删除？`
    )
  }

  private deleteHandle(type: string, rowOrId: any) {
    if (type === '批量' && rowOrId === null) {
      if (this.checkList.length === 0) {
        return this.$message.error('请选择删除对象')
      }
    }
    const id =
      type === '批量'
        ? null
        : rowOrId && typeof rowOrId === 'object'
          ? rowOrId.id
          : rowOrId
    this.$confirm(this.buildDeleteConfirmMessage(type, rowOrId), '确定删除', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning',
      dangerouslyUseHTMLString: true
    }).then(() => {
      deleteDish(type === '批量' ? this.checkList.join(',') : id)
        .then(res => {
          if (res && res.data && res.data.code === 1) {
            this.$message.success('删除成功！')
            this.init()
          } else {
            this.$message.error(res.data.msg)
          }
        })
        .catch(err => {
          this.$message.error('请求出错了：' + err.message)
        })
    })
  }

  private statusHandle(row: any) {
    if (!this.isDishRow(row) && typeof row !== 'string') return
    let params: any = {}
    if (typeof row === 'string') {
      if (this.checkList.length === 0) {
        this.$message.error('批量操作，请先勾选操作商品！')
        return false
      }
      params.id = this.checkList.join(',')
      params.status = row
    } else {
      params.id = row.id
      params.status = row.status ? '0' : '1'
    }
    this.dishState = params
    this.$confirm('确认更改该商品状态?', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(() => {
      dishStatusByStatus(this.dishState)
        .then(res => {
          if (res && res.data && res.data.code === 1) {
            this.$message.success('商品状态已经更改成功！')
            this.init()
          } else {
            this.$message.error(res.data.msg)
          }
        })
        .catch(err => {
          this.$message.error('请求出错了：' + err.message)
        })
    })
  }

  private handleSelectionChange(val: any) {
    const checkArr: any[] = []
    const seen = new Set<string>()
    ;(val || []).forEach((n: any) => {
      if (n && n.nodeType === 'dish' && n.id != null) {
        const key = String(n.id)
        if (seen.has(key)) return
        seen.add(key)
        checkArr.push(n.id)
      }
    })
    this.checkList = checkArr
  }
}
